import React, { useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiPackage,
  FiRefreshCw,
} from 'react-icons/fi';
import request from '../utils/request';
import { formatDate } from '../utils/formatDate';

function currency(value) {
  return (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

export default function ReportedMissingProductsQueue({
  orders = [],
  loading = false,
  error = '',
  onRetry,
  onOrderUpdated,
  onOpenOrder,
}) {
  const [tab, setTab] = useState('pending');
  const [processingKey, setProcessingKey] = useState('');
  const [actionError, setActionError] = useState('');

  const rows = useMemo(
    () =>
      orders.flatMap((order) =>
        (order.reportedMissingProducts || []).map((report) => ({
          order,
          report,
          product: report.product,
          refunded: report.product?.refunded === true,
        }))
      ),
    [orders]
  );

  const pendingRows = useMemo(() => rows.filter((row) => !row.refunded), [rows]);
  const refundedRows = useMemo(() => rows.filter((row) => row.refunded), [rows]);
  const visibleRows = tab === 'pending' ? pendingRows : refundedRows;

  const markRefunded = async (row) => {
    if (!row.product?._id) return;
    const key = `${row.order._id}-${row.product._id}`;
    setProcessingKey(key);
    setActionError('');
    try {
      const { data } = await request.patch(
        `/orders/${row.order._id}/reported-missing-products/${row.product._id}/refunded`
      );
      if (data?.order && onOrderUpdated) onOrderUpdated(data.order);
    } catch (err) {
      setActionError(
        err?.response?.data?.message || 'Unable to mark this product refunded.'
      );
    } finally {
      setProcessingKey('');
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">
            <FiAlertCircle className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Driver-Reported Missing Products
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Mark an item after its refund has been issued. No approval is required.
            </p>
          </div>
        </div>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        ) : null}
      </div>

      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50/60 px-5 py-3 dark:border-gray-700 dark:bg-gray-800/40">
        {[
          { id: 'pending', label: 'Needs refund', count: pendingRows.length },
          { id: 'refunded', label: 'Refunded', count: refundedRows.length },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold ${
              tab === item.id
                ? item.id === 'pending'
                  ? 'bg-rose-500 text-white'
                  : 'bg-emerald-500 text-white'
                : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            {item.label}
            <span className={`rounded-full px-1.5 text-[10px] ${tab === item.id ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
              {item.count}
            </span>
          </button>
        ))}
      </div>

      {error || actionError ? (
        <div className="border-b border-red-200 bg-red-50 px-5 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          {actionError || error}
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-gray-500 dark:text-gray-400">
          <FiRefreshCw className="h-4 w-4 animate-spin" />
          Loading missing products…
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <FiPackage className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {tab === 'pending'
              ? 'No driver-reported products need a refund.'
              : 'No driver-reported products have been marked refunded.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {visibleRows.map((row) => {
            const product = row.product;
            const key = `${row.order._id}-${row.report.productId}`;
            const processing = processingKey === key;
            return (
              <div key={key} className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                    {product?.image ? (
                      <img src={product.image} alt="" className="h-full w-full object-contain" />
                    ) : (
                      <FiPackage className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {product?.name || 'Product no longer found on order'}
                      </span>
                      {row.refunded ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          <FiCheckCircle className="h-3 w-3" /> Refunded
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                          <FiClock className="h-3 w-3" /> Needs refund
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Order #{row.order.uniqueId || String(row.order._id).slice(-6)}
                      {row.order.inStockDate ? ` · ${formatDate(row.order.inStockDate)}` : ''}
                      {product ? ` · Qty ${Number(product.quantity) || 1} · ${currency((Number(product.price) || 0) * (Number(product.quantity) || 1))}` : ''}
                    </div>
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                      Driver report: {row.report.reason || 'Missing or unavailable'}
                    </div>
                    {row.refunded && product?.refundedAt ? (
                      <div className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                        Marked {formatDate(product.refundedAt)}
                        {product.refundedByName ? ` by ${product.refundedByName}` : ''}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-shrink-0 items-center gap-2">
                  {onOpenOrder ? (
                    <button
                      type="button"
                      onClick={() => onOpenOrder(row.order._id)}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                      <FiExternalLink className="h-3.5 w-3.5" />
                      Open order
                    </button>
                  ) : null}
                  {!row.refunded ? (
                    <button
                      type="button"
                      onClick={() => markRefunded(row)}
                      disabled={processing || !product}
                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <FiCheckCircle className="h-3.5 w-3.5" />
                      {processing ? 'Saving…' : 'Mark refunded'}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
